import React, { useEffect, useMemo, useState } from 'react';
import {
  Descriptions,
  Tag,
  Space,
  Button,
  Select,
  Divider,
  Alert,
  Tooltip,
  message,
  Form,
  Input,
  DatePicker,
  Row,
  Col,
} from 'antd';
import {
  DownloadOutlined, LinkOutlined, CheckCircleFilled, MinusCircleOutlined, FileTextOutlined, SearchOutlined, EyeOutlined,
  EditOutlined, SaveOutlined, CloseOutlined, SendOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../../variables/api/axiosInstance';
import { formatDate, formatDateHeure, PV_PROVENANCES, PV_SENS_CIRCULATION, getUser, peutTelechargerPv, HAB, can } from '../../variables/constants';
import DocumentPreviewModal from '../../components/DocumentPreviewModal';
import CBModal from '../../components/CBModal';
import EnvoiPartenaireModal from '../../components/EnvoiPartenaireModal';
import { ChampNumeroPv, NumeroPvAffiche, etatDepuisNumeroPv, nettoyerNumeroPv } from './numeroPv';
import { useTranslation } from 'react-i18next';

const GREEN = '#1a6b3c';
const GREEN_PALE = '#e8f5ed';

const provenanceLabel = (v) =>
  PV_PROVENANCES.find(p => p.value === v)?.label || v || '-';

const sensLabel = (v) =>
  PV_SENS_CIRCULATION.find(s => s.value === v)?.label || v || '-';

const FlagTag = ({ value, label }) => (
  <Tag
    color={value === 'OK' ? 'green' : 'default'}
    icon={value === 'OK' ? <CheckCircleFilled /> : <MinusCircleOutlined />}
    style={{ borderRadius: 20, fontSize: 11, margin: 0 }}
  >
    {label}
  </Tag>
);

/**
 * Modal de détail d'un PV.
 * - Affiche tous les champs métier + statut de complétude.
 * - Permet d'associer un sinistre si le PV est "non-associé".
 * - Permet de télécharger le document scanné (si indexé dans Ossan GED).
 */
export default function DetailPvModal({ open, onClose, pvTrackingId, onUpdated }) {
  const { t } = useTranslation('pvSinistres');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pv, setPv] = useState(null);
  const [form] = Form.useForm();
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  // Association
  const [sinistresOptions, setSinistresOptions] = useState([]);
  const [searchSinistre, setSearchSinistre] = useState('');
  const [selectedSinistre, setSelectedSinistre] = useState(null);
  const [associating, setAssociating] = useState(false);

  // Prévisualisation document
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewBlob, setPreviewBlob] = useState(null);
  const [previewMime, setPreviewMime] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const canDownload = peutTelechargerPv(getUser());

  // Chargement du PV
  useEffect(() => {
    if (!open || !pvTrackingId) return;
    setLoading(true);
    axiosInstance.get(`/v1/pv/${pvTrackingId}`)
      .then(r => setPv(r.data?.data || null))
      .catch(() => setPv(null))
      .finally(() => setLoading(false));
  }, [open, pvTrackingId]);

  // Reset du formulaire d'association à la fermeture
  useEffect(() => {
    if (!open) {
      setSelectedSinistre(null);
      setSearchSinistre('');
      setSinistresOptions([]);
      setEditMode(false);
      form.resetFields();
    }
  }, [open, form]);

  // Pré-remplissage du formulaire d'édition dès que le PV est chargé
  useEffect(() => {
    if (pv) {
      form.setFieldsValue({
        numeroPv: pv.numeroPv,
        etatNumeroPv: etatDepuisNumeroPv(pv.numeroPv),
        sensCirculation: pv.sensCirculation,
        entiteConstatTrackingId: pv.entiteConstatTrackingId,
        dateAccidentPv: pv.dateAccidentPv ? dayjs(pv.dateAccidentPv) : null,
        dateEtablissementPv: pv.dateEtablissementPv ? dayjs(pv.dateEtablissementPv) : null,
        dateReceptionBncb: pv.dateReceptionBncb ? dayjs(pv.dateReceptionBncb) : null,
        lieuAccident: pv.lieuAccident,
        provenance: pv.provenance,
        detailProvenance: pv.detailProvenance,
        referenceSinistreLiee: pv.referenceSinistreLiee,
        aCirconstances: pv.aCirconstances,
        aAuditions: pv.aAuditions,
        aCroquis: pv.aCroquis,
        remarques: pv.remarques,
      });
    }
  }, [pv, form]);

  // Recherche sinistre (mode association)
  useEffect(() => {
    if (!searchSinistre || searchSinistre.length < 2) {
      setSinistresOptions([]);
      return;
    }
    const t = setTimeout(() => {
      axiosInstance.get(`/v1/sinistres?search=${encodeURIComponent(searchSinistre)}&size=10`)
        .then(r => setSinistresOptions(Array.isArray(r.data?.data) ? r.data.data : []))
        .catch(() => setSinistresOptions([]));
    }, 300);
    return () => clearTimeout(t);
  }, [searchSinistre]);

  const estComplet = !!pv?.estComplet;
  const estAssocie = !!pv?.sinistreTrackingId;
  const [transmission, setTransmission] = useState(null); // sinistre du PV à transmettre
  const ouvrirTransmission = async () => {
    try {
      const r = await axiosInstance.get(`/v1/sinistres/${pv.sinistreTrackingId}`);
      setTransmission(r.data?.data || null);
    } catch {
      toast.error(t('detail.messages.dossierIntrouvable'));
    }
  };

  const handleAssocier = async () => {
    if (!selectedSinistre) return;
    setAssociating(true);
    try {
      await axiosInstance.patch(
        `/v1/pv/${pvTrackingId}/associer?sinistreId=${selectedSinistre}`,
      );
      toast.success(t('detail.messages.associe'));
      // recharge le PV
      const r = await axiosInstance.get(`/v1/pv/${pvTrackingId}`);
      setPv(r.data?.data || null);
      setSelectedSinistre(null);
      setSearchSinistre('');
      setSinistresOptions([]);
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('detail.messages.associationImpossible'));
    } finally {
      setAssociating(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const vals = await form.validateFields();
      setSaving(true);
      const payload = {
        // Même filet qu'à la création : le « N° » tapé ou collé ne part pas dans la valeur.
        numeroPv: nettoyerNumeroPv(vals.numeroPv),
        sensCirculation: vals.sensCirculation,
        entiteConstatTrackingId: vals.entiteConstatTrackingId,
        lieuAccident: vals.lieuAccident?.trim(),
        dateAccidentPv: vals.dateAccidentPv?.format('YYYY-MM-DD'),
        dateEtablissementPv: vals.dateEtablissementPv?.format('YYYY-MM-DD') ?? null,
        dateReceptionBncb: vals.dateReceptionBncb?.format('YYYY-MM-DD'),
        provenance: vals.provenance ?? null,
        detailProvenance: vals.detailProvenance?.trim() || null,
        referenceSinistreLiee: vals.referenceSinistreLiee?.trim() || null,
        sinistreTrackingId: pv.sinistreTrackingId ?? null,
        aCirconstances: vals.aCirconstances,
        aAuditions: vals.aAuditions,
        aCroquis: vals.aCroquis,
        remarques: vals.remarques?.trim() || null,
      };
      await axiosInstance.put(`/v1/pv/${pvTrackingId}`, payload);
      toast.success(t('detail.messages.misAJour'));
      const r = await axiosInstance.get(`/v1/pv/${pvTrackingId}`);
      setPv(r.data?.data || null);
      setEditMode(false);
      onUpdated?.();
    } catch (err) {
      if (err?.errorFields) return; // validation inline
      toast.error(err?.response?.data?.message || t('detail.messages.enregistrementImpossible'));
    } finally {
      setSaving(false);
    }
  };

  const fetchDocumentBlob = async () => {
    if (!pv?.pvTrackingId) {
      message.info(t('detail.messages.aucunDocumentDisponible'));
      return null;
    }
    const res = await axiosInstance.get(
      `/v1/pv/${pv.pvTrackingId}/document`,
      { responseType: 'blob' },
    );
    const mime = res.headers?.['content-type'] || guessMimeFromName(pv.documentNomFichier);
    return new Blob([res.data], mime ? { type: mime } : undefined);
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const blob = await fetchDocumentBlob();
      if (!blob) return;
      setPreviewBlob(blob);
      setPreviewMime(blob.type || guessMimeFromName(pv?.documentNomFichier));
      setPreviewOpen(true);
    } catch {
      toast.error(t('detail.messages.previsualisationImpossible'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!canDownload) {
      message.warning(t('detail.messages.nonHabilite'));
      return;
    }
    try {
      const blob = await fetchDocumentBlob();
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = pv.documentNomFichier || `PV_${pv.numeroPv}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('detail.messages.telechargementImpossible'));
    }
  };

  const header = useMemo(() => (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <FileTextOutlined style={{ color: GREEN, fontSize: 18 }} />
        <span style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
          {pv?.numeroPv ? <NumeroPvAffiche numero={pv.numeroPv} /> : t('detail.titre')}
        </span>
        {pv && (
          <Tag
            color={estComplet ? 'green' : 'orange'}
            style={{ borderRadius: 20, fontSize: 11, fontWeight: 600, marginLeft: 6 }}
          >
            {estComplet ? t('commun.complet') : t('commun.incomplet')}
          </Tag>
        )}
      </div>
      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 400, marginTop: 2 }}>
        {pv?.entiteConstatNom || t('detail.uniteInconnue')}
      </div>
    </div>
  ), [pv, estComplet, t]);

  return (
    <>
    <CBModal
      title={header}
      open={open}
      onCancel={onClose}
      width={860}
      footer={
        <Space>
          {!editMode && (
            <>
              <Button onClick={onClose}>{t('commun.fermer')}</Button>
              {pv && (
                <Button icon={<EditOutlined />} onClick={() => setEditMode(true)}>
                  {t('detail.modifier')}
                </Button>
              )}
              {estAssocie && can(HAB.COURRIER_CREATE) && (
                // Le PV part au partenaire du dossier : bureau homologue (ET), compagnie (TE).
                <Button icon={<SendOutlined />} onClick={ouvrirTransmission}>
                  {t('detail.transmettre')}
                </Button>
              )}
              {estAssocie && (
                <Button
                  type="primary"
                  icon={<LinkOutlined />}
                  style={{ background: GREEN, borderColor: GREEN }}
                  onClick={() => navigate(`/sinistres/${pv.sinistreTrackingId}`)}
                >
                  {t('detail.voirDossier')}
                </Button>
              )}
            </>
          )}
          {editMode && (
            <>
              <Button icon={<CloseOutlined />} onClick={() => { setEditMode(false); if (pv) form.resetFields(); }}>
                {t('commun.annuler')}
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={saving}
                onClick={handleSaveEdit}
                style={{ background: GREEN, borderColor: GREEN }}
              >
                {t('detail.enregistrer')}
              </Button>
            </>
          )}
        </Space>
      }
      styles={{ body: { paddingTop: 8, maxHeight: '72vh', overflowY: 'auto' } }}
      destroyOnClose
    >
      {loading && <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>{t('detail.chargement')}</div>}

      {!loading && !pv && (
        <Alert type="error" showIcon message={t('detail.introuvable')} />
      )}

      {pv && (
        <>
          {/* ── Complétude ── */}
          <div
            style={{
              background: estComplet ? GREEN_PALE : '#fff7ed',
              border: `1px solid ${estComplet ? GREEN : '#fdba74'}`,
              borderRadius: 10, padding: '10px 14px', marginBottom: 16,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: estComplet ? GREEN : '#9a3412' }}>
              {t('commun.contenuPv')}
            </div>
            <Space size={6}>
              <FlagTag value={pv.aCirconstances} label={t('commun.circonstances')} />
              <FlagTag value={pv.aAuditions} label={t('commun.auditions')} />
              <FlagTag value={pv.aCroquis} label={t('commun.croquis')} />
            </Space>
          </div>

          {editMode ? (
            <Form form={form} layout="vertical" requiredMark={false}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>{t('detail.identification')}</div>
              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <ChampNumeroPv form={form} label={t('detail.numeroPv')} />
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="sensCirculation" label={t('detail.sens')}>
                    <Select allowClear
                      options={PV_SENS_CIRCULATION.map(s => ({ value: s.value, label: s.label }))} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item name="dateAccidentPv" label={t('detail.dateAccident')}
                    rules={[{ required: true, message: t('detail.requis') }]}>
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                      disabledDate={(d) => d && d.isAfter(dayjs(), 'day')} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  {/* Facultative : les PV anterieurs a ce champ ne la portent pas. */}
                  <Form.Item name="dateEtablissementPv" label={t('commun.dateEtablissement')}>
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                      disabledDate={(d) => d && d.isAfter(dayjs(), 'day')} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="dateReceptionBncb" label={t('detail.dateReception')}
                    rules={[{ required: true, message: t('detail.requis') }]}>
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                      disabledDate={(d) => d && d.isAfter(dayjs(), 'day')} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="lieuAccident" label={t('detail.lieuPrecis')}
                rules={[{ required: true, message: t('detail.requis') }]}>
                <Input.TextArea rows={2} maxLength={500} showCount />
              </Form.Item>

              <Divider style={{ margin: '16px 0' }} />
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>{t('commun.contenuPv')}</div>
              <Row gutter={12}>
                {['aCirconstances', 'aAuditions', 'aCroquis'].map((name, i) => (
                  <Col xs={24} md={8} key={name}>
                    <Form.Item name={name} label={[t('commun.circonstances'), t('commun.auditions'), t('commun.croquis')][i]}>
                      <Select options={[
                        { value: 'OK', label: t('commun.present') },
                        { value: 'NEANT', label: t('commun.neant') },
                      ]} />
                    </Form.Item>
                  </Col>
                ))}
              </Row>

              <Divider style={{ margin: '16px 0' }} />
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>{t('detail.provenance')}</div>
              <Row gutter={12}>
                <Col xs={24} md={10}>
                  <Form.Item name="provenance" label={t('detail.source')}>
                    <Select allowClear
                      options={PV_PROVENANCES.map(p => ({ value: p.value, label: p.label }))} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={14}>
                  <Form.Item name="detailProvenance" label={t('detail.detail')}>
                    <Input maxLength={200} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="referenceSinistreLiee" label={t('commun.referenceExterne')}>
                <Input maxLength={50} />
              </Form.Item>
              <Form.Item name="remarques" label={t('detail.remarques')}>
                <Input.TextArea rows={3} maxLength={1000} showCount />
              </Form.Item>
            </Form>
          ) : (
            <>
              {/* ── Identification ── */}
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>{t('detail.identification')}</div>
              <div style={{
                border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: 20,
              }}>
                {[
                  { label: t('detail.numeroPv'), value: <NumeroPvAffiche numero={pv.numeroPv} /> },
                  { label: t('commun.sensCirculation'), value: sensLabel(pv.sensCirculation) },
                  { label: t('detail.dateAccident'), value: formatDate(pv.dateAccidentPv) },
                  { label: t('commun.dateEtablissement'), value: formatDate(pv.dateEtablissementPv) },
                  { label: t('detail.dateReception'), value: formatDate(pv.dateReceptionBncb) },
                  { label: t('commun.uniteRedactrice'), value: pv.entiteConstatNom || '-', full: true },
                  { label: t('detail.lieuAccident'), value: pv.lieuAccident || '-', full: true },
                ].map(({ label, value, full }, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: full ? '180px 1fr' : '180px 1fr',
                      borderBottom: i < 5 ? '1px solid #e5e7eb' : 'none',
                      background: i % 2 === 0 ? '#fafafa' : '#fff',
                    }}
                  >
                    <div style={{
                      padding: '10px 14px', fontSize: 12, color: '#6b7280',
                      whiteSpace: 'nowrap', borderRight: '1px solid #e5e7eb',
                    }}>
                      {label}
                    </div>
                    <div style={{
                      padding: '10px 14px', fontSize: 13, color: '#111827',
                      wordBreak: 'break-word',
                    }}>
                      {value}
                    </div>
                  </div>
                ))}
              </div>

              <Divider style={{ margin: '20px 0' }} />

              {/* ── Provenance & association ── */}
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>{t('commun.provenanceAssociation')}</div>
              <div style={{
                border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: 20,
              }}>
                {[
                  { label: t('detail.source'), value: provenanceLabel(pv.provenance) },
                  { label: t('detail.detailSource'), value: pv.detailProvenance || '-' },
                  {
                    label: t('commun.sinistreAssocie'),
                    full: true,
                    value: estAssocie ? (
                      <button
                        onClick={() => navigate(`/sinistres/${pv.sinistreTrackingId}`)}
                        style={{
                          background: 'none', border: 'none', color: GREEN,
                          fontWeight: 600, cursor: 'pointer', padding: 0, fontSize: 13,
                        }}
                      >
                        {pv.numeroSinistreLocal}
                      </button>
                    ) : pv.referenceSinistreLiee ? (
                      <span style={{ fontStyle: 'italic', color: '#6b7280' }}>
                        {t('detail.refExterne', { reference: pv.referenceSinistreLiee })}
                      </span>
                    ) : (
                      <Tag color="default" style={{ borderRadius: 20, margin: 0 }}>{t('commun.nonAssocie')}</Tag>
                    ),
                  },
                ].map(({ label, value }, i, arr) => (
                  <div
                    key={i}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '180px 1fr',
                      borderBottom: i < arr.length - 1 ? '1px solid #e5e7eb' : 'none',
                      background: i % 2 === 0 ? '#fafafa' : '#fff',
                    }}
                  >
                    <div style={{
                      padding: '10px 14px', fontSize: 12, color: '#6b7280',
                      whiteSpace: 'nowrap', borderRight: '1px solid #e5e7eb',
                    }}>
                      {label}
                    </div>
                    <div style={{ padding: '10px 14px', fontSize: 13, color: '#111827', wordBreak: 'break-word' }}>
                      {value}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── Association tardive (si non associé) ── */}
          {!estAssocie && (
            <>
              <Divider style={{ margin: '20px 0 12px' }} />
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                <LinkOutlined /> {t('detail.associerSinistre')}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Select
                  showSearch
                  allowClear
                  placeholder={t('commun.rechercheSinistre')}
                  suffixIcon={<SearchOutlined />}
                  filterOption={false}
                  onSearch={setSearchSinistre}
                  value={selectedSinistre}
                  onChange={setSelectedSinistre}
                  style={{ flex: 1 }}
                  notFoundContent={
                    searchSinistre.length < 2 ? t('commun.saisirDeuxCaracteres') : t('commun.aucunSinistre')
                  }
                  options={sinistresOptions.map(s => ({
                    label: (
                      <span>
                        <strong style={{ color: GREEN }}>{s.numeroSinistreLocal}</strong>
                        <span style={{ color: '#6b7280', marginLeft: 8, fontSize: 12 }}>
                          · {s.assureNomComplet || '-'}
                        </span>
                      </span>
                    ),
                    value: s.sinistreTrackingId,
                  }))}
                />
                <Button
                  type="primary"
                  icon={<LinkOutlined />}
                  disabled={!selectedSinistre}
                  loading={associating}
                  style={{ background: GREEN, borderColor: GREEN }}
                  onClick={handleAssocier}
                >
                  {t('detail.associer')}
                </Button>
              </div>
            </>
          )}

          <Divider style={{ margin: '20px 0' }} />

          {/* ── Document scanné ── */}
          <Divider style={{ margin: '20px 0' }} />
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>{t('commun.documentScanne')}</div>

          <div
            style={{
              background: '#fafafa',
              border: '1px solid #e5e7eb',
              borderRadius: 10,
              padding: '12px 16px',
              display: 'grid',
              gridTemplateColumns: 'auto 1fr auto',
              gap: '8px 24px',
              alignItems: 'center',
            }}
          >
            {/* Ligne : État */}
            <span style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap' }}>{t('detail.document.etat')}</span>
            <div style={{ gridColumn: '2 / 4' }}>
              {pv.aDocument ? (
                <Tag color={pv.ossanGedDocumentId ? 'green' : 'gold'} style={{ borderRadius: 20, margin: 0 }}>
                  {pv.ossanGedDocumentId ? t('detail.document.indexe') : t('detail.document.enAttente')}
                </Tag>
              ) : (
                <Tag color="default" style={{ borderRadius: 20, margin: 0 }}>{t('detail.document.aucun')}</Tag>
              )}
            </div>

            {/* Ligne : Nom du fichier */}
            <span style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap' }}>{t('detail.document.nomFichier')}</span>
            <Tooltip title={pv.documentNomFichier || ''}>
              <span
                style={{
                  fontSize: 13,
                  color: '#374151',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                  minWidth: 0,
                }}
              >
                {pv.documentNomFichier || '-'}
              </span>
            </Tooltip>
            <span /> {/* cellule vide col 3 */}

            {/* Ligne : Uploadé le */}
            <span style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap' }}>{t('detail.document.uploadeLe')}</span>
            <span style={{ fontSize: 13, color: '#374151', whiteSpace: 'nowrap' }}>
              {pv.documentUploadedAt ? formatDateHeure(pv.documentUploadedAt) : '-'}
            </span>
            <span /> {/* cellule vide col 3 */}
          </div>

          {/* Boutons */}
          {pv.aDocument && (
            <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Tooltip title={!pv.ossanGedDocumentId ? t('detail.document.disponibleApresSynchro') : ''}>
                <Button
                  icon={<EyeOutlined />}
                  onClick={handlePreview}
                  loading={previewLoading}
                  disabled={!pv.ossanGedDocumentId}
                >
                  {t('detail.document.previsualiser')}
                </Button>
              </Tooltip>
              {canDownload && (
                <Tooltip title={!pv.ossanGedDocumentId ? t('detail.document.disponibleApresSynchro') : ''}>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={handleDownload}
                    disabled={!pv.ossanGedDocumentId}
                  >
                    {t('detail.document.telecharger')}
                  </Button>
                </Tooltip>
              )}
            </div>
          )}

          <DocumentPreviewModal
            open={previewOpen}
            onClose={() => { setPreviewOpen(false); setPreviewBlob(null); }}
            title={pv.documentNomFichier || t('detail.document.titreApercu', { numero: pv.numeroPv })}
            blob={previewBlob}
            mimeType={previewMime}
            fileName={pv.documentNomFichier}
          />

          {!editMode && pv.remarques && (
            <>
              <Divider style={{ margin: '20px 0' }} />
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{t('detail.remarques')}</div>
              <div
                style={{
                  background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: 8,
                  padding: 12, fontSize: 13, whiteSpace: 'pre-wrap', color: '#374151',
                }}
              >
                {pv.remarques}
              </div>
            </>
          )}
        </>
      )}
    </CBModal>
    <EnvoiPartenaireModal
      open={!!transmission}
      sinistre={transmission}
      onClose={() => setTransmission(null)}
      preset={pv ? {
        nature: 'TRANSMISSION_PV',
        // Objet et lettre rédigés dans la langue du partenaire choisi dans la fenêtre
        // (documents:envoiPartenaire.modeles.transmissionPv) ; en français, le texte d'avant.
        modele: {
          objet: 'transmissionPv.objet',
          corps: 'transmissionPv.corps',
          valeurs: { numeroPv: pv.numeroPv || '', numero: transmission?.numeroSinistreLocal || '' },
        },
        documentsGedIds: pv.ossanGedDocumentId ? [pv.ossanGedDocumentId] : [],
      } : null}
    />
    </>
  );
}

function guessMimeFromName(name) {
  if (!name) return null;
  const ext = String(name).toLowerCase().split('.').pop();
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'png') return 'image/png';
  return null;
}
